export class NetworkError extends Error {}

export class RequestError extends Error {
	status = 0;

	/**
	 * @param {number} status
	 * @param {string} text
	 */
	constructor(status, text) {
		super(text);
		this.status = status;
	}

	get json() {
		try {
			return JSON.parse(this.message);
		} catch {
			return { message: this.message };
		}
	}
}

/**
 * @typedef {object} RequestOptions
 * @property {object} [json]
 * @property {BodyInit} [body]
 * @property {Record<string, string | string[] | number | undefined>} [query]
 * @property {string} [authorization]
 * @property {object} [headers]
 * @property {RequestCache} [cache]
 */

/**
 * @param {"GET" | "POST" | "PUT" | "PATCH" | "DELETE"} method
 * @param {string} path
 * @param {RequestOptions} [options]
 * @returns {Promise<unknown>}
 */
export default async function request(method, path, options = {}) {
	const url = new URL(path);
	if (options.query) {
		for (const [key, value] of Object.entries(options.query)) {
			if (typeof value === "string" || typeof value === "boolean") url.searchParams.set(key, value);
			else if (typeof value === "number") url.searchParams.set(key, `${value}`);
			else if (Array.isArray(value)) for (const v of value) url.searchParams.append(key, v);
		}
	}

	/** @type {RequestInit} */
	const init = { method, cache: options.cache };

	if (options.body) init.body = options.body;
	else if (options.json) init.body = JSON.stringify(options.json);

	init.credentials = "include";
	init.headers = {};
	init.headers["content-type"] = "application/json";
	if (options.authorization) init.headers.authorization = `Bearer ${options.authorization}`;
	if (options.headers) Object.assign(init.headers, options.headers);

	const res = await fetch(url, init).catch(e => {
		if (e instanceof Error) throw new NetworkError(e.message, e);
		throw new NetworkError(e);
	});

	// if the request failed, throw a RequestError
	if (!res.ok) throw new RequestError(res.status, await res.text().catch(reason => reason));

	// if the status is 204, there's no content
	if (res.status === 204) return;

	// otherwise, return the JSON
	return await res.json();
}
